<template>
    <div>
        <div class="flex justify-center w-full">
            <custom-tooltip bottom border>
                <app-button icon circle @click="openNewFilter">
                    <img
                        src="@/assets/images/filter.svg"
                        height="15"
                        width="15"
                        alt="reload img"
                    />
                </app-button>

                <template #tooltip> Modificar filtros de búsqueda </template>
            </custom-tooltip>
        </div>

        <div class="flex flex-col mt-1">
            <div
                v-if="activeFilters.includes('keyword')"
                class="md:w-6/12 w-8/12 mx-auto mb-4"
            >
                <custom-input
                    v-model="keyword"
                    type="text"
                    placeholder="Buscar por palabra clave .."
                    class="mr-2"
                    @blur="applyFilter"
                    @enter="onEnter"
                >
                    <template #icon> 🔎 </template>
                </custom-input>
            </div>

            <div
                v-if="activeFilters.includes('sku')"
                class="md:w-6/12 w-8/12 mx-auto mb-4"
            >
                <custom-input
                    v-model="sku"
                    type="text"
                    placeholder="SKU"
                    class="mr-2"
                    @blur="applyFilter"
                    @enter="onEnter"
                >
                    <template #icon> 🔎 </template>
                </custom-input>
            </div>

            <div
                v-if="activeFilters.includes('barcode')"
                class="md:w-6/12 w-8/12 mx-auto mb-4"
            >
                <barcode-scanner-input
                    v-model="barcode"
                    enabled
                    @change="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('line')"
                class="md:w-6/12 w-8/12 mx-auto mb-4"
            >
                <subline-selector
                    v-model="line"
                    :default-filters="{}"
                    enabled
                    @change="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('brand')"
                class="md:w-6/12 w-8/12 mx-auto mb-4"
            >
                <brand-selector v-model="brand" enabled @change="applyFilter" />
            </div>

            <div
                v-if="activeFilters.includes('stock')"
                class="md:w-6/12 w-8/12 mx-auto mb-4"
            >
                <p class="text-sm text-theme-muted font-semibold mb-1">
                    📦 Stock
                </p>
                <div class="flex gap-2">
                    <custom-input
                        v-model="stockStart"
                        type="number"
                        placeholder="Desde"
                        @blur="applyFilter"
                        @enter="onEnter"
                    />
                    <custom-input
                        v-model="stockEnd"
                        type="number"
                        placeholder="Hasta"
                        @blur="applyFilter"
                        @enter="onEnter"
                    />
                </div>
            </div>

            <div
                v-if="activeFilters.includes('priceOffer')"
                class="md:w-6/12 w-8/12 mx-auto mb-4"
            >
                <p class="text-sm text-theme-muted font-semibold mb-1">
                    💰 Precio de venta
                </p>
                <div class="flex gap-2">
                    <custom-input
                        v-model="priceOfferStart"
                        type="number"
                        placeholder="Desde"
                        @blur="applyFilter"
                        @enter="onEnter"
                    />
                    <custom-input
                        v-model="priceOfferEnd"
                        type="number"
                        placeholder="Hasta"
                        @blur="applyFilter"
                        @enter="onEnter"
                    />
                </div>
            </div>

            <div
                v-if="activeFilters.includes('active')"
                class="md:w-6/12 w-8/12 mx-auto mb-4 flex justify-center"
            >
                <span>Producto activo &nbsp;</span>
                <custom-checkbox v-model="active" warn @input="applyFilter" />
            </div>
        </div>

        <app-dialog v-model="isOpenModal" size="lg" @close="pushNewFilter">
            <template #header>
                <div class="flex items-center justify-between gap-4">
                    <h3 class="text-base font-semibold text-theme-base">
                        Selecciona los filtros
                    </h3>

                    <app-button
                        :disabled="!filters.length"
                        @click="isOpenModal = false"
                    >
                        ✔️ Aplicar filtros ({{ filters.length }})
                    </app-button>
                </div>
            </template>
            <cards-selector v-model="filters" :base="baseFilters" />
        </app-dialog>
    </div>
</template>

<script>
import { productsFilters } from "@/common/fields/filters-query";

import AppDialog from "@/components/utils/AppDialog.vue";
import CardsSelector from "@/components/utils/CardsSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

export default {
    name: "ProductsFilters",
    components: {
        AppDialog,
        CardsSelector,
        CustomInput,
        BrandSelector: () => import("@/components/brands/BrandSelector.vue"),
        SublineSelector: () => import("@/components/lines/SublineSelector.vue"),
        BarcodeScannerInput: () =>
            import("@/components/utils/BarcodeScannerInput.vue")
    },
    model: {
        prop: "itemsCurrent",
        event: "change"
    },
    props: {
        itemsCurrent: {
            default: () => ({}),
            type: Object,
            require: true
        },
        enabledFilters: {
            default: () => [
                "brand",
                "line",
                "keyword",
                "active",
                "barcode",
                "sku"
            ],
            type: Array,
            require: true
        },
        defaultFilters: {
            default: () => [],
            type: Array,
            require: true
        }
    },
    emits: ["change", "enter"],
    data: () => ({
        activeFilters: [],
        isOpenModal: false,
        keyword: "",
        barcode: "",
        sku: "",
        active: true,
        stockStart: "",
        stockEnd: "",
        priceOfferStart: "",
        priceOfferEnd: "",
        brand: { slug: "marca-blanca", name: "Marca Blanca" },
        line: { slug: "otros", name: "Otros" },
        filters: [],
        baseFilters: []
    }),
    computed: {
        stockRange() {
            return this.buildRange(this.stockStart, this.stockEnd);
        },
        priceOfferRange() {
            return this.buildRange(this.priceOfferStart, this.priceOfferEnd);
        }
    },
    watch: {
        enabledFilters(value) {
            this.setEnabledFilters(value);
        }
    },
    created() {
        this.setEnabledFilters(this.enabledFilters);
    },
    mounted() {
        this.setInput(this.itemsCurrent);
        if (!this.activeFilters.length) {
            this.activeFilters = [...this.defaultFilters];
        }
        this.applyFilter();
    },
    methods: {
        openNewFilter() {
            this.isOpenModal = true;
        },
        pushNewFilter() {
            this.activeFilters = [...this.filters];
            this.applyFilter();
        },
        applyFilter() {
            this.$emit("change", {
                ...{
                    ...(this.activeFilters.includes("line")
                        ? { lineSlug: this.line.slug }
                        : null)
                },
                ...{
                    ...(this.activeFilters.includes("brand")
                        ? { brandSlug: this.brand.slug }
                        : null)
                },
                ...{
                    ...(this.activeFilters.includes("keyword")
                        ? { keyword: this.keyword }
                        : null)
                },
                ...{
                    ...(this.activeFilters.includes("barcode")
                        ? { barcode: this.barcode }
                        : null)
                },
                ...{
                    ...(this.activeFilters.includes("sku")
                        ? { sku: this.sku }
                        : null)
                },
                ...{
                    ...(this.activeFilters.includes("active")
                        ? { active: this.active }
                        : null)
                },
                ...{
                    ...(this.activeFilters.includes("stock") && this.stockRange
                        ? { stock: this.stockRange }
                        : null)
                },
                ...{
                    ...(this.activeFilters.includes("priceOffer") &&
                    this.priceOfferRange
                        ? { priceOffer: this.priceOfferRange }
                        : null)
                }
            });
        },
        buildRange(start, end) {
            const range = {};
            if (start !== "" && start !== null) range.start = Number(start);
            if (end !== "" && end !== null) range.end = Number(end);
            return Object.keys(range).length ? range : null;
        },
        onEnter() {
            this.applyFilter();
            this.$emit("enter");
        },
        setInput(value) {
            const filters = Object.keys(value);
            this.activeFilters = filters;
            this.filters = filters;
            if ("keyword" in value) this.keyword = value.keyword;
            if ("sku" in value) this.sku = value.sku;
            if ("barcode" in value) this.barcode = value.barcode;
            if ("active" in value) this.active = value.active;
            if ("lineSlug" in value) {
                this.line = { slug: value.lineSlug, name: value.lineSlug };
            }
            if ("brandSlug" in value) {
                this.brand = { slug: value.brandSlug, name: value.brandSlug };
            }
            if ("stock" in value) {
                this.stockStart = value.stock.start ?? "";
                this.stockEnd = value.stock.end ?? "";
            }
            if ("priceOffer" in value) {
                this.priceOfferStart = value.priceOffer.start ?? "";
                this.priceOfferEnd = value.priceOffer.end ?? "";
            }
        },
        setEnabledFilters(value) {
            this.baseFilters = productsFilters.filter((item) => {
                const hasPermission =
                    !item.permissions ||
                    item.permissions.every((permission) =>
                        this.$ability.hasPermission(permission)
                    );
                return (
                    value.includes(item.id) &&
                    (item.show === undefined || item.show) &&
                    hasPermission
                );
            });
        }
    }
};
</script>
